import React from "react";
import { Check } from "lucide-react";
import Header from "../components/site/Header";
import Seo from "../components/site/Seo";
import Footer from "../components/site/Footer";
import CTAButton from "../components/site/CTAButton";
import Photo from "../components/site/Photo";
import ScrollReveal from "../components/site/ScrollReveal";
import {
  PlannerBlock, MelbourneSection, FAQSection, FinalCTA, Breadcrumb, SisterServices,
} from "../components/site/ServiceSections";
import InternalRail from "../components/site/InternalRail";
import { blogsForService } from "../data/internalLinks";

export default function UberEatsPhotography() {
  return (
    <div className="min-h-screen bg-ink text-bone">
      <Seo />
      <Header />
      <Hero />
      <WhyMatters />
      <AppGrid />
      <Categories />
      <PlatformSpecs />
      <PlannerBlock
        eyebrow="Plan it now · Interactive estimator"
        headline="Cost your full delivery menu"
        italic="in under a minute."
        blurb="Slide for menu size, tick the items you actually sell on apps, see your indicative price. Most delivery menu shoots come in between $540 and $1,000."
      />
      <MelbourneSection
        serviceLabel="Uber Eats Photography"
        eyebrow="Melbourne delivery · Mobile coverage"
        headline="A delivery-app photographer for"
        italic="every Melbourne kitchen."
        intro="Delivery-app photography for kitchens across Greater Melbourne — CBD, Footscray, Glen Waverley, Dandenong, Brunswick, Carlton, Richmond, Point Cook, Tarneit and Werribee. Same-day uploads on request."
      />
      <SisterServices exclude="ubereats" keys={["menu", "cafe", "gbp", "reels"]} />
      <InternalRail
        eyebrow="Related reading · The Journal"
        headline="More on Uber Eats"
        italic="and delivery."
        theme="ink"
        cols={3}
        items={blogsForService("ubereats-photography")}
      />
      <FAQSection
        eyebrow="Common questions · Uber Eats photography Melbourne"
        headline="Crops, uploads,"
        italic="and turnaround."
        intro="Most delivery menu shoots are confirmed in under five minutes on the phone. If you're behind on uploads ahead of a campaign, mention the deadline — we routinely turn around in 72 hours."
        faqs={FAQS}
      />
      <FinalCTA
        eyebrow="Final step · Hand-confirmed quotes"
        headline="Ready to lift your"
        italic="delivery-app orders?"
        intro="Quick form below or call directly. We'll often do a 5-minute audit of your current Uber Eats menu before quoting — to scope exactly what needs reshooting."
        backdropSlug="menu-butter-chicken"
        formTitle="Tell us about your delivery menu."
      />
      <Footer />
    </div>
  );
}

const Hero = () => (
  <section className="relative min-h-[100svh] overflow-hidden bg-ink">
    <div className="absolute inset-0">
      <Photo slug="menu-butter-chicken" alt="Uber Eats photography Melbourne — butter chicken hero plate" priority ratio="auto" className="!relative h-full w-full" />
    </div>
    <div className="absolute inset-0 bg-gradient-to-b from-ink/75 via-ink/40 to-ink" />
    <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/30 to-transparent" />

    <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10 min-h-[100svh] flex flex-col pt-24 sm:pt-28 lg:pt-32 pb-10 lg:pb-12">
      <Breadcrumb current="Uber Eats Photography Melbourne" />

      <div className="flex-1 flex flex-col justify-center max-w-5xl">
        <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-5 sm:mb-7 font-bold">
          Uber Eats Photography Melbourne · DoorDash · Menulog
        </p>

        <h1 className="font-display tracking-editorial leading-[0.92]" style={{ fontSize: "clamp(2.5rem, 7.5vw, 6.5rem)" }}>
          Photos built for
          <br />
          the <span className="italic font-light text-ember">scroll.</span>
        </h1>

        <p className="mt-6 sm:mt-8 max-w-2xl text-base sm:text-lg md:text-xl leading-relaxed text-bone/85">
          App-graded photography for Uber Eats, DoorDash and Menulog. Square
          crops, consistent white-balance across the menu, hero items lit to
          earn the tap. Shot on-location, uploaded the same week.
        </p>

        <p className="mt-7 max-w-xl">
          <span className="inline-block bg-ember text-bone font-bold text-sm md:text-base uppercase tracking-wider2 px-4 py-2.5">
            Delivery Photos From Only $10 Per Image
            <span className="text-bone/70 ml-1">*</span>
          </span>
        </p>
        <p className="mt-3 text-xs text-bone/55 max-w-md leading-relaxed">
          * Volume tier — selected larger menu shoots. 15-image minimum.
          Smaller menus from $30 per image.
        </p>

        <div className="mt-8 sm:mt-10 flex flex-wrap gap-3">
          <a href="#enquiry" className="w-full sm:w-auto">
            <CTAButton variant="primary" size="lg" className="w-full sm:w-auto">Get A Shoot Quote</CTAButton>
          </a>
          <a href="#planner" className="w-full sm:w-auto">
            <CTAButton variant="ghost" size="lg" arrow={false} className="w-full sm:w-auto">Audit My Delivery Menu</CTAButton>
          </a>
        </div>
      </div>

      <div className="mt-10 lg:mt-12 grid grid-cols-3 gap-4 md:gap-10 max-w-3xl border-t border-bone/15 pt-6">
        {[
          ["Min. shoot", "15 images"],
          ["Turnaround", "5–7 days"],
          ["Uploads", "Apps-ready files"],
        ].map(([k, v]) => (
          <div key={k}>
            <p className="text-[10px] sm:text-xs uppercase tracking-wider2 text-ember mb-1.5 font-bold">{k}</p>
            <p className="font-sans font-bold text-base sm:text-lg md:text-xl leading-tight tracking-tight">{v}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const WhyMatters = () => (
  <section className="relative bg-ink text-bone py-24 lg:py-36 overflow-hidden">
    <div className="absolute inset-0 grain opacity-50 pointer-events-none" />
    <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 lg:gap-16">
        <div className="col-span-12 lg:col-span-7">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            Why delivery-app photos decide your orders
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.75rem)" }}>
            Delivery menus are
            <br />
            <span className="italic font-light text-ember">graded by photo.</span>
          </h2>

          <div className="mt-12 grid gap-7 text-bone/85 leading-relaxed text-base md:text-lg">
            <ScrollReveal>
              <p>
                A customer scrolling Uber Eats spends roughly <strong className="text-bone">fourteen seconds</strong> on
                your menu before they decide. The food is the same — the
                photograph is what closes (or loses) the order. A single
                strong hero image at the top of the menu often does the work
                of three average ones combined.
              </p>
            </ScrollReveal>
            <ScrollReveal delay={120}>
              <p>
                We shoot specifically for the app spec — square crops,
                consistent white-balance across every dish, no busy
                backgrounds, no plastic gloss. Files arrive named to match
                your menu items so uploads take minutes, not hours.
              </p>
            </ScrollReveal>
            <ScrollReveal delay={200}>
              <p>
                Built for Uber Eats, DoorDash and Menulog out of the box —
                with bonus square + landscape crops for your Google Business
                Profile, website and Instagram.
              </p>
            </ScrollReveal>

            <ScrollReveal delay={280}>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 pt-4">
                {[
                  "Uber Eats · DoorDash · Menulog",
                  "Square + landscape crops",
                  "Consistent white-balance",
                  "Files named to your menu",
                  "Hero-item lighting",
                  "Same-week upload support",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-3">
                    <Check size={16} strokeWidth={2} className="text-ember mt-1 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </ScrollReveal>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-5 space-y-3 md:space-y-5 lg:mt-4">
          <Photo slug="menu-malai-tikka" alt="Uber Eats photography Melbourne — hero plate" ratio="4/5" sizes="(min-width: 1024px) 35vw, 100vw" />
          <div className="grid grid-cols-2 gap-3 md:gap-5">
            <Photo slug="menu-veg-crispy" alt="DoorDash photography Melbourne" ratio="1/1" sizes="20vw" />
            <Photo slug="menu-fried-rice" alt="Menulog photography Melbourne" ratio="1/1" sizes="20vw" />
          </div>
        </div>
      </div>
    </div>
  </section>
);

const AppGrid = () => (
  <section className="bg-bone text-ink py-24 lg:py-36">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 lg:gap-12 mb-14 lg:mb-16 items-end">
        <div className="col-span-12 lg:col-span-6">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            Built for the app · Square-first
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.75rem)" }}>
            Tap-worthy at
            <br />
            <span className="italic font-light text-ember">thumb size.</span>
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-6 lg:pb-3">
          <p className="text-char text-base md:text-lg leading-relaxed">
            A delivery-app image is judged in a 80×80px thumbnail before it's
            ever judged full-screen. We shoot tight, bright, and direct — so
            your menu reads at the smallest possible size and rewards the
            second look.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
        {["menu-butter-chicken", "menu-malai-tikka", "menu-veg-crispy", "menu-chinese-bhel-03", "hero-laddu-220", "menu-fried-rice"].map((s) => (
          <Photo key={s} slug={s} alt={`Uber Eats photography Melbourne — ${s}`} ratio="1/1" sizes="(min-width: 1024px) 16vw, 50vw" />
        ))}
      </div>
    </div>
  </section>
);

const CATEGORIES = [
  { n: "01", title: "Hero items", slug: "menu-malai-tikka", desc: "The single dish that sits at the top of your Uber Eats menu — lit, plated and cropped to outperform anything else on the page." },
  { n: "02", title: "Curries & mains", slug: "menu-butter-chicken", desc: "Saucy, layered dishes shot to look as rich on a thumbnail as they do in the bowl. Square-first composition." },
  { n: "03", title: "Snacks & sides", slug: "menu-veg-crispy", desc: "Chips, samosas, momos, garlic bread — the smaller-ticket items that lift average order value when shot well." },
  { n: "04", title: "Burgers & wraps", slug: "menu-chinese-bhel-03", desc: "Stack, sauce, sear and side — burger and wrap photography that reads from a thumb. Real builds, no plastic styling." },
  { n: "05", title: "Drinks & desserts", slug: "menu-falooda", desc: "The upsells. Shot with the same care as the mains because they're what tips the order over $40." },
  { n: "06", title: "Packaged-as-served", slug: "menu-fried-rice", desc: "Photographed in the actual packaging your customer receives — for delivery menus that match the experience." },
  { n: "07", title: "Family & banquet", slug: "hero-banquet-01", desc: "Family-sized banquets, sharing platters and feeds — composed to look like a group order, not a single dish." },
  { n: "08", title: "Brand collateral", slug: "hero-raj-kachori-08", desc: "Cover photo, banner, in-app advert, Google listing — every supporting asset your delivery presence needs, shot in one session." },
];

const Categories = () => (
  <section className="bg-ink text-bone py-24 lg:py-36">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 mb-14 lg:mb-20 items-end">
        <div className="col-span-12 lg:col-span-7">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            What we photograph · Delivery-app menu
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.75rem)" }}>
            Every item on your
            <br />
            <span className="italic font-light text-ember">delivery menu.</span>
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-5 lg:pb-3">
          <p className="text-bone/75 text-base md:text-lg leading-relaxed max-w-md">
            From hero items to add-on snacks. One on-location day, one
            consistent visual style, files ready to upload to every app you're
            on.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-bone/10 border border-bone/10">
        {CATEGORIES.map((c) => (
          <article key={c.n} className="bg-ink hover:bg-[#161613] transition-colors p-5 md:p-6 flex flex-col">
            <Photo slug={c.slug} alt={`${c.title} — Uber Eats photography Melbourne`} ratio="4/5" sizes="(min-width: 1024px) 22vw, 50vw" />
            <div className="mt-5 flex flex-col flex-1">
              <span className="font-display italic text-2xl text-ember leading-none">{c.n}</span>
              <h3 className="font-display text-xl md:text-2xl tracking-editorial leading-tight mt-2.5">{c.title}</h3>
              <p className="mt-3 text-sm text-bone/70 leading-relaxed flex-1">{c.desc}</p>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

const PLATFORM_SPECS = [
  { name: "Uber Eats", res: "800×800 px", ratio: "1:1 square", bg: "White / light neutral", size: "Under 2 MB", fmt: "JPG" },
  { name: "DoorDash", res: "1000×1000 px", ratio: "1:1 square", bg: "White preferred", size: "Under 5 MB", fmt: "JPG / PNG" },
  { name: "Menulog", res: "800×600 px", ratio: "4:3 landscape", bg: "Flexible", size: "Under 2 MB", fmt: "JPG" },
];

const PlatformSpecs = () => (
  <section className="bg-bone text-ink py-24 lg:py-36 border-t border-ink/10">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 mb-12 items-end">
        <div className="col-span-12 lg:col-span-7">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            Platform spec sheet · Early 2026
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5vw, 4rem)" }}>
            The exact specs your
            <br />
            <span className="italic font-light text-ember">thumbnails must hit.</span>
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-5 lg:pb-3">
          <p className="text-char text-base md:text-lg leading-relaxed max-w-md">
            Get any of these wrong and the platform's auto-compression makes it worse, not better. Every delivery shoot we run exports to all three sets in parallel.
          </p>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b-2 border-ink">
              <th className="text-left py-4 pr-4 text-xs uppercase tracking-wider2 font-bold text-ember">Platform</th>
              <th className="text-left py-4 pr-4 text-xs uppercase tracking-wider2 font-bold text-ember">Min. resolution</th>
              <th className="text-left py-4 pr-4 text-xs uppercase tracking-wider2 font-bold text-ember">Aspect ratio</th>
              <th className="text-left py-4 pr-4 text-xs uppercase tracking-wider2 font-bold text-ember">Background</th>
              <th className="text-left py-4 pr-4 text-xs uppercase tracking-wider2 font-bold text-ember">Max file size</th>
              <th className="text-left py-4 text-xs uppercase tracking-wider2 font-bold text-ember">Format</th>
            </tr>
          </thead>
          <tbody>
            {PLATFORM_SPECS.map((p) => (
              <tr key={p.name} className="border-b border-ink/15">
                <td className="py-5 pr-4 font-display text-xl tracking-editorial">{p.name}</td>
                <td className="py-5 pr-4 text-sm md:text-base text-char">{p.res}</td>
                <td className="py-5 pr-4 text-sm md:text-base text-char">{p.ratio}</td>
                <td className="py-5 pr-4 text-sm md:text-base text-char">{p.bg}</td>
                <td className="py-5 pr-4 text-sm md:text-base text-char">{p.size}</td>
                <td className="py-5 text-sm md:text-base text-char">{p.fmt}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-8 text-sm text-char-mute italic max-w-2xl">
        Platform requirements shift periodically. Verify against each platform's restaurant partner page before bulk-uploading. Specs above current as of February 2026.
      </p>
    </div>
  </section>
);

const FAQS = [
  { q: "What's different about shooting for Uber Eats vs. menu photography?", a: "Same shoot, different crops and lighting brief. Uber Eats prioritises square crops, tight framing, bold colour and high contrast at thumbnail size — everything has to read at 80×80 pixels. We deliver app-ready files alongside the standard menu library." },
  { q: "Will the photos work on DoorDash and Menulog too?", a: "Yes. We deliver square + landscape crops sized for Uber Eats, DoorDash, Menulog, Google Business Profile and your website. One shoot, every channel." },
  { q: "Do you help upload the photos to the apps?", a: "Yes — files arrive named to match your existing menu items so uploads are a drag-and-drop job. If you want, we can also walk a marketing team member through the upload remotely after delivery." },
  { q: "How much does Uber Eats photography cost in Melbourne?", a: "Volume-tier pricing: $30 per image for 15–20 images, $25 per image for 21–99 images, and $10 per image once you hit 100+ images (minimum spend $1,000). Most delivery menu shoots fall between $540 and $1,000." },
  { q: "How fast can you turn around for a launch?", a: "Standard delivery is 5–7 business days. Urgent 72-hour turnarounds available for campaigns and new menu launches — flag the deadline on the call." },
  { q: "Will the images lift orders?", a: "Generally yes — venues that reshoot their delivery menu typically see a 20–40% lift in add-to-cart on the dishes that were reshot. We don't promise specific lifts because pricing, ratings, delivery zones and competition all play in too." },
];
